import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design03/Header';
import {
  Sparkles,
  Globe,
  Crown,
  Shield,
  Star,
  MapPin,
  Mail,
  Phone,
  Users,
  Truck,
  CheckCircle,
  ArrowRight,
  Award,
  Leaf,
  Heart,
  Zap,
  Diamond,
  Target,
  TrendingUp,
} from 'lucide-react';

const HomePage = () => {
  return (
    <>
      {/* Head tags: React 19 hoists these into <head> */}
      <title>Claude&apos;s Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices</title>
      <meta
        name="description"
        content="Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
      />
      <meta
        name="keywords"
        content="Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import"
      />
      <meta property="og:title" content="Claude's Special - Premium Ceylon Cinnamon Exports" />
      <meta
        property="og:description"
        content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
      />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://claudesspecial.com/" />

      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90"></div>
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
              alt="Ceylon Cinnamon plantation"
              className="w-full h-full object-cover object-center"
            />
          </div>
          {/* Luxury Decorative Elements */}
          <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-br from-rose-400/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-gradient-to-br from-purple-400/15 to-indigo-500/15 rounded-full blur-2xl animate-pulse delay-500"></div>
          <div className="absolute top-1/4 right-1/4 w-24 h-24 bg-gradient-to-br from-emerald-400/15 to-teal-500/15 rounded-full blur-2xl animate-pulse delay-700"></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-4 pt-28 pb-20">
          {/* Luxury Badge */}
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full px-10 py-5 mb-12 border border-amber-400/30 shadow-2xl hover:bg-gradient-to-r hover:from-amber-500/30 hover:to-yellow-500/30 transition-all duration-500">
            <Diamond className="h-7 w-7 text-amber-400" />
            <span className="text-lg font-bold">Luxury Ceylon Heritage Since 1999</span>
            <Sparkles className="h-6 w-6 text-amber-400 animate-pulse" />
          </div>

          <h1 className="text-8xl md:text-9xl lg:text-[12rem] font-black mb-10 leading-none drop-shadow-2xl">
            <span className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
              Claude&apos;s
            </span>
            <br />
            <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
              Special
            </span>
          </h1>

          <p className="text-4xl md:text-5xl mb-10 font-light text-amber-200 drop-shadow-lg">
            Luxury Ceylon Cinnamon Excellence
          </p>

          <p className="text-2xl mb-20 opacity-90 max-w-5xl mx-auto leading-relaxed drop-shadow-md text-gray-200">
            From Sri Lanka&apos;s mystical highlands to Australia&apos;s finest establishments,
            we deliver the world&apos;s most exquisite Ceylon Cinnamon with uncompromising luxury and authenticity.
          </p>

          <div className="flex flex-col sm:flex-row gap-10 justify-center items-center mb-24">
            <Link
              to="/ceylon-cinnamon"
              className="group bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-600 hover:via-yellow-500 hover:to-amber-600 text-slate-900 px-16 py-8 rounded-full font-black text-2xl transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-4"
            >
              <span>Discover Excellence</span>
              <ArrowRight className="h-7 w-7 group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
            <a
              href="#about"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-amber-400/50 hover:border-amber-400/80 px-16 py-8 rounded-full font-black text-2xl transition-all duration-500 backdrop-blur-xl shadow-xl"
            >
              Our Legacy
            </a>
          </div>

          {/* Luxury Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pt-20 pb-20 border-t border-amber-400/30">
            <div className="text-center group hover:scale-110 transition-transform duration-500">
              <div className="text-5xl md:text-6xl font-black text-amber-400 mb-4 drop-shadow-lg">25+</div>
              <div className="text-lg text-amber-200 font-bold">Years Excellence</div>
            </div>
            <div className="text-center group hover:scale-110 transition-transform duration-500">
              <div className="text-5xl md:text-6xl font-black text-amber-400 mb-4 drop-shadow-lg">100%</div>
              <div className="text-lg text-amber-200 font-bold">Pure Ceylon</div>
            </div>
            <div className="text-center group hover:scale-110 transition-transform duration-500">
              <div className="text-5xl md:text-6xl font-black text-amber-400 mb-4 drop-shadow-lg">50+</div>
              <div className="text-lg text-amber-200 font-bold">Global Partners</div>
            </div>
            <div className="text-center group hover:scale-110 transition-transform duration-500">
              <div className="text-5xl md:text-6xl font-black text-amber-400 mb-4 drop-shadow-lg">99.9%</div>
              <div className="text-lg text-amber-200 font-bold">Luxury Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-slate-900 relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-gradient-to-br from-amber-500/30 to-yellow-500/30 rounded-3xl transform rotate-12"></div>
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl border border-amber-500/20">
                <img
                  src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                  alt="Traditional cinnamon processing"
                  className="w-full h-96 object-cover rounded-2xl mb-8"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl p-8 shadow-xl text-slate-900">
                  <div className="text-center">
                    <Diamond className="h-10 w-10 mx-auto mb-3" />
                    <div className="text-2xl font-black">Luxury</div>
                    <div className="text-sm opacity-80">Quality</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full px-6 py-3 mb-8 border border-amber-400/30">
                <Crown className="h-5 w-5" />
                <span className="text-sm font-bold">Our Heritage</span>
              </div>

              <h2 className="text-6xl font-black text-white mb-8 leading-tight">
                Crafting Luxury
                <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Since 1999</span>
              </h2>

              <p className="text-2xl text-gray-300 mb-12 leading-relaxed">
                Born from a passion for authentic Sri Lankan heritage, Claude&apos;s Special has become
                the premier bridge between Ceylon&apos;s ancient cinnamon traditions and Australia&apos;s
                most discerning luxury markets.
              </p>

              <div className="space-y-8 mb-12">
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-xl">
                    <Award className="h-8 w-8 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">Certified Excellence</h3>
                    <p className="text-gray-300 text-lg">Premium ISO certification with HACCP compliance for the highest international luxury standards.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">Artisan Partnership</h3>
                    <p className="text-gray-300 text-lg">Exclusive relationships with master craftsmen ensuring fair trade and sustainable luxury.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-xl">
                    <Globe className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">Global Luxury</h3>
                    <p className="text-gray-300 text-lg">Seamless premium export operations connecting Sri Lankan excellence to Australian luxury markets.</p>
                  </div>
                </div>
              </div>

              <Link
                to="/ceylon-cinnamon"
                className="inline-flex items-center bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-10 py-5 rounded-full font-black text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl space-x-3"
              >
                <span>Discover More</span>
                <ArrowRight className="h-6 w-6" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full px-8 py-4 mb-8 border border-amber-400/30">
              <Star className="h-6 w-6" />
              <span className="font-bold text-lg">Why Choose Luxury</span>
            </div>
            <h2 className="text-6xl font-black text-white mb-8">
              Unmatched Luxury &amp;
              <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Excellence</span>
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto">
              Every aspect of our operation is designed to deliver the finest luxury Ceylon Cinnamon experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-amber-500/20 hover:border-amber-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Shield className="h-10 w-10 text-slate-900" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Premium Quality Assurance</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Every batch undergoes rigorous luxury testing and quality control to ensure only the finest Ceylon Cinnamon reaches your customers.
                </p>
                <div className="flex items-center text-amber-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>ISO Luxury Certified</span>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-rose-500/20 hover:border-rose-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Zap className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Express Luxury Export</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Premium logistics and priority customs clearance ensure your luxury orders reach Australia with exceptional speed.
                </p>
                <div className="flex items-center text-rose-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>5-10 Day Express</span>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-emerald-500/20 hover:border-emerald-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Heart className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Sustainable Luxury</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Exclusive partnerships with artisan farmers ensure ethical luxury practices and environmental sustainability.
                </p>
                <div className="flex items-center text-emerald-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Ethical Luxury Certified</span>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-blue-500/20 hover:border-blue-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Truck className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Packaging</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Bespoke packaging solutions from premium bulk to luxury retail-ready formats to meet your exclusive needs.
                </p>
                <div className="flex items-center text-blue-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Bespoke Solutions</span>
                </div>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-purple-500/20 hover:border-purple-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Target className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Concierge Support</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Dedicated luxury export specialists provide personalized concierge support throughout your premium import journey.
                </p>
                <div className="flex items-center text-purple-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>24/7 Concierge</span>
                </div>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="group bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-orange-500/20 hover:border-orange-400/40 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <TrendingUp className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Premium Value</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Direct luxury sourcing and efficient operations allow us to offer premium Ceylon Cinnamon at competitive wholesale prices.
                </p>
                <div className="flex items-center text-orange-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Premium Value Promise</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-rose-500/20 to-pink-500/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 border border-amber-400/30">
            <Diamond className="h-6 w-6 text-amber-400" />
            <span className="text-white font-bold text-lg">Ready for Luxury?</span>
          </div>

          <h2 className="text-6xl md:text-7xl font-black text-white mb-8 leading-tight">
            Begin Your Luxury
            <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Ceylon Journey</span>
          </h2>

          <p className="text-2xl text-gray-200 mb-16 leading-relaxed max-w-4xl mx-auto">
            Join Australia&apos;s most prestigious importers who trust Claude&apos;s Special for authentic luxury Ceylon Cinnamon.
            Experience premium wholesale pricing and concierge export support.
          </p>

          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <a
              href="#contact"
              className="group bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-600 hover:via-yellow-500 hover:to-amber-600 text-slate-900 px-12 py-6 rounded-full font-black text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-3"
            >
              <span>Get Luxury Quote</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
            </a>
            <Link
              to="/ceylon-cinnamon"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-amber-400/50 hover:border-amber-400/80 px-12 py-6 rounded-full font-black text-xl transition-all duration-300 backdrop-blur-xl"
            >
              Explore Our Luxury
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full px-8 py-4 mb-8 border border-amber-400/30">
              <Mail className="h-6 w-6" />
              <span className="font-bold text-lg">Luxury Contact</span>
            </div>
            <h2 className="text-6xl font-black text-white mb-8">
              Ready to Import
              <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Luxury Ceylon Cinnamon?</span>
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto">
              Contact our luxury export specialists for premium wholesale pricing and bespoke solutions
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-20">
            {/* Contact Info */}
            <div className="space-y-10">
              <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 border border-amber-500/20">
                <h3 className="text-3xl font-bold text-white mb-8">Luxury Export Office</h3>
                <div className="space-y-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-8 w-8 text-slate-900" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2 text-xl">Location</h4>
                      <p className="text-gray-300 text-lg">Colombo, Sri Lanka</p>
                      <p className="text-amber-400">Premium Export Processing Zone</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2 text-xl">Phone</h4>
                      <p className="text-gray-300 text-lg">+94 11 XXX XXXX</p>
                      <p className="text-rose-400">Luxury Export Hotline</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2 text-xl">Email</h4>
                      <p className="text-gray-300 text-lg">exports@claudesspecial.lk</p>
                      <p className="text-purple-400">24/7 Concierge Response</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-3xl p-10 border border-amber-400/30">
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Export Specialties</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300">Premium Cinnamon Sticks</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300">Luxury Ground Powder</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300">Pure Essential Oil</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300">Bespoke Packaging</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl border border-amber-500/20">
              <h3 className="text-3xl font-bold text-white mb-8">Luxury Export Inquiry</h3>
              <form className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-lg font-bold text-white mb-3">Company Name *</label>
                    <input
                      type="text"
                      className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200"
                      placeholder="Your luxury company"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-bold text-white mb-3">Contact Person *</label>
                    <input
                      type="text"
                      className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200"
                      placeholder="Your name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-bold text-white mb-3">Email Address *</label>
                  <input
                    type="email"
                    className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200"
                    placeholder="your@luxurycompany.com"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-lg font-bold text-white mb-3">Product Interest</label>
                    <select className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200">
                      <option value="">Select luxury product</option>
                      <option value="sticks">Premium Cinnamon Sticks</option>
                      <option value="powder">Luxury Ground Powder</option>
                      <option value="oil">Pure Essential Oil</option>
                      <option value="mixed">Mixed Luxury Products</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-lg font-bold text-white mb-3">Quantity Required</label>
                    <select className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200">
                      <option value="">Select quantity</option>
                      <option value="1-100kg">1-100 kg</option>
                      <option value="100-500kg">100-500 kg</option>
                      <option value="500-1000kg">500-1000 kg</option>
                      <option value="1000kg+">1000+ kg</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-bold text-white mb-3">Message</label>
                  <textarea
                    rows={5}
                    className="w-full px-6 py-4 rounded-2xl border border-amber-500/30 bg-slate-700 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-colors duration-200 resize-none"
                    placeholder="Tell us about your luxury import requirements, preferred packaging, delivery timeline, etc."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 py-5 rounded-2xl font-black text-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center justify-center space-x-3"
                >
                  <span>Send Luxury Inquiry</span>
                  <ArrowRight className="h-6 w-6" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-20 border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center">
                  <Sparkles className="h-9 w-9 text-slate-900" />
                </div>
                <div>
                  <div className="text-3xl font-black text-white">Claude&apos;s Special</div>
                  <div className="text-amber-400 font-bold">Luxury Ceylon Cinnamon</div>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-8 max-w-md text-lg">
                Bringing the authentic luxury and health benefits of Ceylon Cinnamon from Sri Lanka&apos;s highlands
                to Australia&apos;s most discerning luxury markets with uncompromising quality and service.
              </p>
              <div className="flex space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer">
                  <span className="text-lg font-bold">🇱🇰</span>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer">
                  <span className="text-lg font-bold">🇦🇺</span>
                </div>
              </div>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-xl font-bold mb-8 text-amber-400">Luxury Products</h4>
              <ul className="space-y-4 text-gray-300">
                <li className="hover:text-white transition-colors duration-200 cursor-pointer text-lg">Premium Ceylon Sticks</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer text-lg">Luxury Ground Powder</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer text-lg">Pure Essential Oil</li>
                <li className="hover:text-white transition-colors duration-200 cursor-pointer text-lg">Bespoke Packaging</li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xl font-bold mb-8 text-amber-400">Quick Links</h4>
              <ul className="space-y-4">
                <li>
                  <Link to="/ceylon-cinnamon" className="text-gray-300 hover:text-white transition-colors duration-200 text-lg">
                    About Ceylon Cinnamon
                  </Link>
                </li>
                <li>
                  <a href="#contact" className="text-gray-300 hover:text-white transition-colors duration-200 text-lg">
                    Luxury Export Inquiry
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-gray-300 hover:text-white transition-colors duration-200 text-lg">
                    Our Legacy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-amber-500/20 pt-10 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-6 md:mb-0 text-lg">
              &copy; 2025 Claude&apos;s Special. All rights reserved. | Luxury Export License: SL-LUX-2025-001
            </p>
            <div className="flex items-center space-x-8 text-gray-400">
              <span className="flex items-center space-x-2">
                <Diamond className="h-5 w-5 text-amber-400" />
                <span>100% Luxury Ceylon</span>
              </span>
              <span className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-rose-400" />
                <span>Premium Certified</span>
              </span>
              <span className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-blue-400" />
                <span>Global Luxury</span>
              </span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default HomePage;
